<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  UiButton,
  UiFormBuilder,
  UiIconButton,
  UiInfoBox,
  UiText,
  UiTooltip,
  type BuilderRowConfig,
} from '@shopexperts/shared';
import type { z } from 'zod';

const props = withDefaults(defineProps<{
  title: string;
  /** Shown above the form while editing, to say what the fields drive. */
  description?: string;
  /** Rows of the edit form. No rows means the section is read-only. */
  rows?: BuilderRowConfig[];
  schema?: z.ZodType<any>;
  initialValues?: Record<string, unknown>;
  /** Another section is being edited; this one stays read-only until it closes. */
  locked?: boolean;
  saving?: boolean;
  serverErrors?: Record<string, string | undefined>;
  formError?: string | null;
}>(), {
  description: undefined,
  rows: () => [],
  schema: undefined,
  initialValues: undefined,
  locked: false,
  saving: false,
  serverErrors: undefined,
  formError: null,
});

const editing = defineModel<boolean>('editing', { default: false });

const emit = defineEmits<{
  save: [values: Record<string, unknown>];
  cancel: [];
  /** Any keystroke after a failed save, so the parent can drop stale server errors. */
  input: [values: Record<string, unknown> | undefined];
}>();

const model = ref<Record<string, unknown>>();
const isDirty = ref(false);

const editable = computed(() => props.rows.length > 0);
const tooltip = computed(() =>
  props.locked ? 'Finish the section you are editing first' : `Edit ${props.title.toLowerCase()}`,
);

watch(model, (values) => {
  if (editing.value) emit('input', values);
}, { deep: true });

function cancel() {
  editing.value = false;
  emit('cancel');
}

function setValue(name: string, value: unknown) {
  if (!model.value) return;
  model.value = { ...model.value, [name]: value };
}

defineExpose({ setValue });
</script>

<template>
  <section>
    <div class="flex items-center justify-between gap-3 mb-3">
      <div class="flex items-center gap-2 min-w-0">
        <UiText as="h3" variant="caption" class="text-gray-400 tracking-wide" :text="title.toUpperCase()" />
        <slot name="meta" />
      </div>
      <UiTooltip v-if="editable && !editing" :content="tooltip">
        <UiIconButton
          icon="pencil"
          :aria-label="`Edit ${title.toLowerCase()}`"
          :disabled="locked"
          class="text-gray-400 hover:text-gray-700"
          @click="editing = true"
        />
      </UiTooltip>
    </div>

    <template v-if="editing">
      <UiText
        v-if="description"
        as="p"
        variant="p-small"
        class="text-gray-500 mb-4"
        :text="description"
      />

      <UiFormBuilder
        :rows="rows"
        :schema="schema"
        :initial-values="initialValues"
        :server-errors="serverErrors"
        v-model="model"
        v-model:dirty="isDirty"
        gap="4"
        scroll-to-first-error
        @submit="emit('save', $event)"
      >
        <template #actions>
          <UiInfoBox v-if="formError" variant="error">
            <UiText as="span" variant="p-small">{{ formError }}</UiText>
          </UiInfoBox>
          <div class="flex items-center justify-end gap-2">
            <UiButton variant="outline" size="sm" label="Cancel" :disabled="saving" @click="cancel" />
            <UiButton
              variant="primary"
              size="sm"
              type="submit"
              :label="saving ? 'Saving…' : 'Save changes'"
              :disabled="saving || !isDirty"
            />
          </div>
        </template>
      </UiFormBuilder>
    </template>

    <slot v-else />
  </section>
</template>
